import { FormEvent, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useConvexAuth } from 'convex/react'
import { useAuthActions } from '@convex-dev/auth/react'
import { useEffect } from 'react'
import { appUrl } from '../lib/config'
import { Spinner } from './ui/Spinner'
import { BrandLogo } from './ui/BrandLogo'

export function Login() {
  const { signIn } = useAuthActions()
  const { isAuthenticated, isLoading } = useConvexAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const claim = params.get('claim') || ''
  const next = params.get('next') || '/admin'

  // Where to land after auth — carry the claim through so the editor can apply it.
  const postAuth = claim ? `/admin?claim=${encodeURIComponent(claim)}` : next

  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Already signed in (e.g. returning user hitting /login) → go straight in.
  useEffect(() => {
    if (!isLoading && isAuthenticated) navigate(postAuth, { replace: true })
  }, [isLoading, isAuthenticated, navigate, postAuth])

  async function oauth(provider: 'google' | 'twitter') {
    setError(null)
    await signIn(provider, { redirectTo: appUrl(postAuth) })
  }

  async function sendCode(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setPending(true)
    try {
      await signIn('resend', { email })
      setStep('code')
    } catch {
      setError('Could not send the code. Check the email and try again.')
    } finally {
      setPending(false)
    }
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setPending(true)
    try {
      await signIn('resend', { email, code })
      navigate(postAuth, { replace: true })
    } catch {
      setError('Invalid or expired code.')
    } finally {
      setPending(false)
    }
  }

  if (isLoading || isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-900 p-8">
        <Link to="/" className="mb-6 flex justify-center text-white no-underline">
          <BrandLogo />
        </Link>
        {claim && (
          <p className="mb-5 rounded-lg bg-fuchsia-500/10 px-3 py-2 text-center text-sm text-fuchsia-300">
            Sign in to claim <strong>/{claim}</strong>
          </p>
        )}

        <button
          onClick={() => oauth('google')}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg bg-white py-3 text-sm font-semibold text-zinc-900 hover:bg-zinc-100"
        >
          Continue with Google
        </button>
        <button
          onClick={() => oauth('twitter')}
          className="mb-5 flex w-full items-center justify-center gap-2 rounded-lg bg-sky-500 py-3 text-sm font-semibold text-white hover:bg-sky-400"
        >
          Continue with X
        </button>

        <div className="mb-5 flex items-center gap-3 text-xs text-zinc-600">
          <div className="h-px flex-1 bg-zinc-800" />
          or
          <div className="h-px flex-1 bg-zinc-800" />
        </div>

        {step === 'email' ? (
          <form onSubmit={sendCode}>
            <label className="mb-1 block text-xs font-medium text-zinc-400">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mb-3 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-3 text-sm text-white outline-none focus:border-fuchsia-500"
            />
            <button
              type="submit"
              disabled={pending}
              className="flex w-full items-center justify-center rounded-lg bg-fuchsia-500 py-3 text-sm font-bold text-white hover:bg-fuchsia-400 disabled:opacity-50"
            >
              {pending ? <Spinner className="h-4 w-4" /> : 'Email me a code'}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode}>
            <label className="mb-1 block text-xs font-medium text-zinc-400">
              Enter the code sent to {email}
            </label>
            <input
              inputMode="numeric"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className="mb-3 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-3 text-center text-lg tracking-[0.4em] text-white outline-none focus:border-fuchsia-500"
            />
            <button
              type="submit"
              disabled={pending}
              className="flex w-full items-center justify-center rounded-lg bg-fuchsia-500 py-3 text-sm font-bold text-white hover:bg-fuchsia-400 disabled:opacity-50"
            >
              {pending ? <Spinner className="h-4 w-4" /> : 'Verify & continue'}
            </button>
            <button
              type="button"
              onClick={() => setStep('email')}
              className="mt-3 w-full text-center text-xs text-zinc-500 hover:text-zinc-300"
            >
              Use a different email
            </button>
          </form>
        )}

        {error && <p className="mt-4 text-center text-sm text-rose-400">{error}</p>}
      </div>
    </div>
  )
}
